import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText
} from "@mui/material";
import { ResponsivePie } from "@nivo/pie";
import LeafletAnalyticsMap from "../components/LeafletAnalyticsMap";
import { fetchRealtimeAnalytics, fetchRecentActivities } from "../api/analyticsApi";
import Grid from '@mui/material/GridLegacy';

// Type Definitions
interface Activity {
  activity: string;
  created_at: string;
}

interface CountryData {
  name: string;
  count: number;
}

interface DeviceData {
  id: string;
  value: number;
}

const RealtimeAnalytics = () => {
  const [liveVisitors, setLiveVisitors] = useState<number>(0);
  const [pageViews, setPageViews] = useState<Activity[]>([]);
  const [devices, setDevices] = useState<DeviceData[]>([]);
  const [countries, setCountries] = useState<CountryData[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const analytics = await fetchRealtimeAnalytics();
        const activities = await fetchRecentActivities();

        setLiveVisitors(analytics.activeCustomers || 0);
        setPageViews(activities || []);
        setDevices(Array.isArray(analytics.devices) ? analytics.devices : []);
        setCountries(Array.isArray(analytics.countries) ? analytics.countries : []);
      } catch (error) {
        console.error("Error fetching real-time analytics data:", error);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Real-time Analytics
      </Typography>

      <Grid container spacing={3}>
        {/* Map Section */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ height: 400, p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Visitor Locations
            </Typography>
            <LeafletAnalyticsMap />
          </Paper>
        </Grid>

        {/* Statistics Section */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Live Visitors
            </Typography>
            <Typography variant="h3">{liveVisitors}</Typography>
          </Paper>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Device Breakdown
            </Typography>
            <Box sx={{ height: 200 }}>
              {devices && devices.length > 0 ? (
                <ResponsivePie
                  data={devices}
                  margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                  innerRadius={0.5}
                  padAngle={0.7}
                  cornerRadius={3}
                  colors={{ scheme: "nivo" }}
                  borderWidth={1}
                  borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                  enableArcLabels={false}
                  arcLinkLabelsSkipAngle={10}
                  arcLinkLabelsTextColor="#333333"
                  arcLinkLabelsThickness={2}
                  arcLinkLabelsColor={{ from: "color" }}
                />
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No device data
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Page Views */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Active Page Views
            </Typography>
            <List dense>
              {pageViews.map((activity, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={activity.activity}
                    secondary={`${Math.floor(Math.random() * 60)}s ago`}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Country Distribution */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Country Distribution
            </Typography>
            <List dense>
              {countries.map((country, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={country.name}
                    secondary={`${country.count} visitors`}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RealtimeAnalytics;
